import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './AddMembersModal.css';

interface Friend {
  id: number;
  first_name: string;
  last_name: string;
  profile_photo?: string;
}

interface AddMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  friends: Friend[];
  selectedIds: number[];
  onToggleSelection: (id: number) => void;
  onConfirm: () => void;
  loading?: boolean;
  getPhotoUrl: (photo?: string) => string | null;
}

/**
 * Shared modal component for adding members to a list/group
 * Used in both Map.tsx (ListPanel) and Friends.tsx
 */
const AddMembersModal: React.FC<AddMembersModalProps> = ({
  isOpen,
  onClose,
  friends,
  selectedIds,
  onToggleSelection,
  onConfirm,
  loading = false,
  getPhotoUrl,
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter friends by search query
  const filteredFriends = useMemo(() => {
    if (!searchQuery.trim()) return friends;
    const query = searchQuery.toLowerCase();
    return friends.filter(friend =>
      `${friend.first_name} ${friend.last_name}`.toLowerCase().includes(query)
    );
  }, [friends, searchQuery]);

  // Reset search when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="add-members-modal-overlay" onClick={onClose}>
      <div className="add-members-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-members-modal-header">
          <h2>{t('friends.groups.addMembers')}</h2>
          <button className="add-members-modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="add-members-search">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
            <circle cx="10" cy="10" r="7"/>
            <line x1="15" y1="15" x2="22" y2="22"/>
          </svg>
          <input
            type="text"
            placeholder={t('friends.groups.searchFriendsPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Friends List */}
        <div className="add-members-list">
          {filteredFriends.length === 0 ? (
            <p className="add-members-empty">
              {friends.length === 0
                ? t('friends.groups.allFriendsInGroup')
                : t('map.search.noResults')
              }
            </p>
          ) : (
            filteredFriends.map((friend) => {
              const isSelected = selectedIds.includes(friend.id);
              const photoUrl = getPhotoUrl(friend.profile_photo);
              const initials = `${friend.first_name?.[0] || ''}${friend.last_name?.[0] || ''}`.toUpperCase();

              return (
                <div
                  key={friend.id}
                  className={`add-members-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => onToggleSelection(friend.id)}
                >
                  <div className="add-members-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelection(friend.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="add-members-avatar">
                    {photoUrl ? (
                      <img src={photoUrl} alt={`${friend.first_name} ${friend.last_name}`} />
                    ) : (
                      <div className="add-members-avatar-placeholder">{initials}</div>
                    )}
                  </div>
                  <span className="add-members-name">
                    {friend.first_name} {friend.last_name}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="add-members-modal-footer">
          <button
            className="btn-primary"
            onClick={onConfirm}
            disabled={loading || selectedIds.length === 0}
          >
            {loading ? t('common.loading') : `${t('common.add')} (${selectedIds.length})`}
          </button>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMembersModal;
